import type { CreateInlineReplyMutationType } from '@confluence/inline-comments-queries/entry-points/CreateInlineReplyMutation.ts';
import type { SitePermissionType } from '@confluence/comments-data-manager/entry-points/useAllCommentsDataQueryTypes.ts';

type ReplyLocation = {
	type: string;
	inlineMarkerRef?: string;
	inlineResolveProperties: {
		resolvedUser?: string;
		resolvedTime?: number;
	};
};

export type CommentContributor = {
	__typename?: string | null;
	accountId?: string | null;
	displayName?: string | null;
	profilePicture?: {
		path?: string | null;
	} | null;
} | null;

export type CommentWithContributors = {
	contributors?: ReadonlyArray<CommentContributor> | null;
};

export const hasContributors = (comment: unknown): comment is CommentWithContributors =>
	typeof comment === 'object' && comment !== null && 'contributors' in comment;

export type Reply = {
	id: string;
	ancestors: object[];
	author: {
		accountId: string;
		displayName: string;
		profilePicture: {
			path: string;
		};
		permissionType: SitePermissionType;
	};
	body: {
		value: string;
	};
	createdAt: {
		value: string;
	};
	createdAtNonLocalized: string;
	contentStatus: string;
	links: {
		webui: string;
		editui: string;
	};
} & Record<'location', ReplyLocation> & {
		parentId: string | null;
		permissions: {
			isEditable: boolean;
			isRemovable: boolean;
			isResolvable: boolean;
		};
		version: {
			by: {
				displayName: string;
			};
			when: string;
			number: number;
		};
	};

export type Comment = Reply & {
	replies: Reply[];
};

export type InlineCommentError = {
	error: Error;
	action: 'delete' | 'resolve' | 'reopen';
};

// Defines the mode of a single comment
export type CommentMode = 'view' | 'edit' | 'create' | 'reply';

// Defines the mode of all inline comments
export type InlineCommentsMode = 'view-all' | 'view' | 'edit';

export type CommentPermissions = {
	isEditable?: boolean;
	isRemovable?: boolean;
	isResolvable?: boolean;
};

export type CommentAction = 'resolve' | 'delete' | 'edit' | 'reopen';

export type UserToMention = {
	accountId: string;
	displayName: string;
};

export type MutationResult = {
	data?: CreateInlineReplyMutationType | boolean | null;
};
